import type {Notification, Organization, PublicUser, User} from '@perfectpost/perfect-post-common';
import {useMemo} from 'react';

import {useMeQuery} from './queries/useMeQuery';
import {useSession} from './SessionProvider';

const EMPTY_ORGANIZATIONS: Organization[] = [];

/**
 * Hooks composés du domaine `session`.
 *
 * | Ancien sélecteur Redux              | Hook ici                       |
 * |-------------------------------------|--------------------------------|
 * | `selectUser` / `state.main.user`    | `useUser`                      |
 * | `state.main.loadingUser`            | `useIsUserLoading`             |
 * | `state.main.organizations`          | `useOrganizations`             |
 * | `state.main.notifications`          | `useNotifications`             |
 * | `state.main.lnAccessTokenValid`     | `useLinkedInTokenValid`        |
 * | `selectLocale`                      | `useLocale`                    |
 * | `selectPremium`                     | `usePremium`                   |
 * | `selectPremiumStatus`               | `usePremiumStatus`             |
 * | `selectIsPaused`                    | `useIsPaused`                  |
 * | `selectPausedUntil`                 | `usePausedUntil`               |
 * | `selectShouldShowTrialUI`           | `useShouldShowTrialUI`         |
 * | `selectIsPremiumUser`               | `useIsPremiumUser`             |
 * | `selectOrganizationsWithPermission` | `useOrganizationsWithPermission` |
 * | `selectOrganizationPermission`      | `useOrganizationPermission`    |
 * | `selectUserInOrganization`          | `useUserInOrganization`        |
 * | `selectOrganizationsInCommon`       | `useOrganizationsInCommon`     |
 */

export function useUser(): User | undefined {
  return useMeQuery().data?.me;
}

export function useIsUserLoading(): boolean {
  return useMeQuery().isLoading;
}

export function useOrganizations(): Organization[] {
  return useMeQuery().data?.organizations ?? EMPTY_ORGANIZATIONS;
}

export function useNotifications(): Notification | undefined {
  return useMeQuery().data?.notifications;
}

export function useLinkedInTokenValid(): boolean | undefined {
  return useMeQuery().data?.valid;
}

export function useLocale(): NonNullable<User['locale']> {
  return useUser()?.locale ?? 'en';
}

export function usePremiumStatus() {
  return useSession().premiumStatus;
}

export function usePremium(): boolean {
  const premiumStatus = usePremiumStatus();
  return premiumStatus !== 'free' && premiumStatus !== 'paused';
}

export function useIsPaused(): boolean {
  return usePremiumStatus() === 'paused';
}

export function usePausedUntil(): string | undefined {
  return useSession().subscriptionPausedUntil;
}

export function useShouldShowTrialUI(): boolean {
  return usePremiumStatus() === 'trial';
}

export function useIsPremiumUser(): boolean {
  return usePremiumStatus() === 'premium';
}

export function useOrganizationsWithPermission() {
  const organizations = useOrganizations();
  const user = useUser();
  return useMemo(
    () =>
      organizations.map((organization) => {
        const userOrg = organization.users.find((u) => u.user === user?._id);
        return {...organization, userPermissions: userOrg};
      }),
    [organizations, user],
  );
}

export function useOrganizationPermission(organizationId: string | undefined) {
  const organizations = useOrganizationsWithPermission();
  return useMemo(
    () => organizations.find((item) => item._id === organizationId)?.userPermissions,
    [organizations, organizationId],
  );
}

export function useUserInOrganization(organizationId: string | undefined) {
  const organizations = useOrganizations();
  return useMemo(
    () => organizations.find((item) => item._id === organizationId)?.users ?? [],
    [organizations, organizationId],
  );
}

export function useOrganizationsInCommon(user: PublicUser | undefined) {
  const organizations = useOrganizations();
  return useMemo(
    () => organizations.filter((organization) => organization.users.some((u) => u.user === user?._id)),
    [organizations, user],
  );
}
